// What Brill has already written for her, compressed enough to ride along in
// every generation's prompt.
//
// The generator wrote each post blind, so it repeated itself: same pillar, same
// opening move, same story, with nothing in the prompt saying "you already said
// this." Sending her posts back fixes that. Sending them WHOLE does not scale —
// at 200 saved posts that's roughly 110k tokens and ~45c a generation, and
// burying her brand foundation under that much old copy makes instruction
// following worse, not better.
//
// So: a one-line digest of every post (~45 tokens each, every field already
// stored — no migration), plus the full text of the few most recent, which is
// where "don't sound like last week" actually lives.

/* ~7k tokens of digest. A coach posting daily reaches this in about five months;
   past it we keep the newest and SAY SO in the prompt, because a cap the model
   can't see is a cap that quietly changes the answer. */
const MAX_DIGEST_ROWS = 150;
const FULL_TEXT_COUNT = 5;
const FULL_TEXT_CLAMP = 1200;   // chars per post — long posts get their opening, which is what repeats

/** Newest first. `createdAt` is set by recordStore on every row; scheduledDate is
 *  optional and only used to break ties on rows written in the same instant. */
function byNewestFirst(a, b) {
  const at = a.createdAt || a.scheduledDate || "";
  const bt = b.createdAt || b.scheduledDate || "";
  if (at === bt) return 0;
  return at < bt ? 1 : -1;
}

function digestLine(p) {
  const i = p.inputs || {};
  const parts = [];
  const day = (p.createdAt || p.scheduledDate || "").slice(0, 10);
  if (day) parts.push(day);
  parts.push(p.title || "Untitled post");
  if (i.pillar) parts.push(`Pillar: ${i.pillar}`);
  if (i.theme) parts.push(`Theme: ${i.theme}`);
  if (i.hookCategory) parts.push(`Hook: ${i.hookCategory}`);
  if (i.cta && i.cta !== "none") parts.push(`CTA: ${i.cta}`);
  return `- ${parts.join(" · ")}`;
}

/**
 * Build the prompt-ready history block for one platform.
 *
 * @param posts        every saved post (the store's `rows`)
 * @param platform     target platform; history is filtered to it, because what
 *                     reads as repetition on LinkedIn is invisible on Instagram
 * @param excludeArcId when generating inside an arc, that arc's own posts are
 *                     already in the conversation thread. Re-sending them would
 *                     duplicate them AND rewrite this cached block on every turn,
 *                     which is exactly what the cache split exists to prevent.
 * @returns {{count: number, text: string|null}} `count` drives the UI line so an
 *          empty history can't pass unnoticed; `text` goes in the prompt.
 */
export function buildPostHistory(posts, { platform, excludeArcId = null } = {}) {
  const relevant = (Array.isArray(posts) ? posts : [])
    .filter((p) => p && typeof p.caption === "string" && p.caption.trim())
    .filter((p) => !platform || p.platform === platform)
    .filter((p) => !excludeArcId || p.arcId !== excludeArcId)
    .sort(byNewestFirst);

  if (!relevant.length) return { count: 0, text: null };

  const recent = relevant.slice(0, FULL_TEXT_COUNT);
  const earlier = relevant.slice(FULL_TEXT_COUNT, FULL_TEXT_COUNT + MAX_DIGEST_ROWS);
  const omitted = Math.max(0, relevant.length - FULL_TEXT_COUNT - MAX_DIGEST_ROWS);

  /* Two instructions, deliberately opposed, because these posts serve two purposes.
     As CONTENT they are what to avoid. As WRITING they are the closest thing in the
     prompt to how she actually sounds on this platform — the full-text ones are the
     captions she saved, edits included, so they are her published voice rather than
     a description of it. One "do not repeat" line was pushing away from both. */
  const out = [
    `POSTS SHE HAS ALREADY WRITTEN FOR THIS PLATFORM — ${relevant.length} in total.`,
    "CONTENT — do not repeat: not the angle, not the opening move, not the anecdote. If one is worth returning to, refer back to it as something she has already said rather than saying it again.",
    "VOICE — do match: these are her posts as she published them, so treat them as the truest sample of how she writes here. Follow their register — sentence length, formality, how much she qualifies, whether she uses lists or line breaks, how she opens and closes. Where they and the voice guidance above differ, these are more recent evidence. Match how she writes, never what she wrote about.",
    "",
    "MOST RECENT, IN FULL (newest first):",
    ...recent.map((p) => {
      const body = p.caption.trim();
      const clipped = body.length > FULL_TEXT_CLAMP ? `${body.slice(0, FULL_TEXT_CLAMP).trimEnd()}…` : body;
      return `---\n${clipped}`;
    }),
  ];

  if (earlier.length) {
    out.push("", "EARLIER POSTS (one line each, newest first):", ...earlier.map(digestLine));
  }
  if (omitted) {
    out.push("", `(${omitted} older post${omitted === 1 ? "" : "s"} not listed.)`);
  }

  return { count: relevant.length, text: out.join("\n") };
}

/**
 * Pick the pillar, theme and hook for a "let Brill choose" generation.
 *
 * This replaced three `Math.random()` calls. Random re-picks what she just posted
 * about roughly a third of the time, which is the specific complaint. Counting
 * what history actually used and taking the least-used rotates instead: on a
 * fresh account it walks pillar 0, 1, 2, and after that it genuinely favours
 * whatever has gone quietest.
 *
 * Ties break on the lowest index so the same inputs always give the same answer —
 * otherwise this is untestable.
 */
export function suggestNextPost(posts, { pillars = [], hookIds = [], platform } = {}) {
  const history = (Array.isArray(posts) ? posts : [])
    .filter((p) => p && (!platform || p.platform === platform));

  const tally = (values, key, rows = history) => {
    const counts = new Map(values.map((v) => [v, 0]));
    rows.forEach((p) => {
      const used = (p.inputs || {})[key];
      if (counts.has(used)) counts.set(used, counts.get(used) + 1);
    });
    return counts;
  };

  let pillarIdx = null;
  let theme = null;
  if (pillars.length) {
    const labels = pillars.map((p) => p.label);
    const counts = tally(labels, "pillar");
    pillarIdx = labels.reduce(
      (best, label, i) => (counts.get(label) < counts.get(labels[best]) ? i : best),
      0
    );
    const themes = pillars[pillarIdx]?.themes || [];
    if (themes.length) {
      /* Counted only over posts that used THIS pillar. Themes belong to a pillar,
         and two pillars can carry the same theme word — tallying globally lets
         one pillar's usage silence an identically-named theme under another. */
      const withinPillar = history.filter((p) => (p.inputs || {}).pillar === labels[pillarIdx]);
      const themeCounts = tally(themes, "theme", withinPillar);
      theme = themes.reduce(
        (best, t) => (themeCounts.get(t) < themeCounts.get(best) ? t : best),
        themes[0]
      );
    }
  }

  let hookCatId = null;
  if (hookIds.length) {
    const hookCounts = tally(hookIds, "hookCategory");
    hookCatId = hookIds.reduce(
      (best, h) => (hookCounts.get(h) < hookCounts.get(best) ? h : best),
      hookIds[0]
    );
  }

  return { pillarIdx, theme, hookCatId };
}
